// components/private/order/PaymentSummary.tsx

import { useEffect, useState } from "react";
import {
    CreditCard,
    Globe,
    CheckCircle2,
    Clock,
    XCircle,
    AlertCircle,
    Package,
    // ShieldCheck,
    // ChevronRight,
    Timer
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface PaymentSummaryProps {
    total: number;
    method?: string;
    packageName?: string;
    status?: string;
    isUsd?: boolean;
    transactionId?: string;
    date?: string;
    expiryDate?: string;
}

export function PaymentSummary({
    total,
    method = "Transfer Bank",
    packageName = "Paket Layanan",
    status = "unpaid",
    isUsd = false,
    transactionId = "-",
    date = "-",
    expiryDate
}: PaymentSummaryProps) {
    const [timeLeft, setTimeLeft] = useState<string>("");
    const s = status.toLowerCase();

    useEffect(() => {
        if (!expiryDate || s === "paid") {
            setTimeLeft("");
            return;
        }

        // Validasi format tanggal
        const target = new Date(expiryDate).getTime();
        if (isNaN(target)) {
            setTimeLeft("");
            return;
        }

        const timer = setInterval(() => {
            const now = new Date().getTime();
            const distance = target - now;

            if (distance < 0) {
                setTimeLeft("KEDALUWARSA");
                clearInterval(timer);
                return;
            }

            // Kalkulasi waktu
            const days = Math.floor(distance / (1000 * 60 * 60 * 24));
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);

            // Format tampilan: jika ada hari, tampilkan hari dan jam saja agar tidak terlalu panjang
            // Jika sudah di bawah 1 hari, tampilkan j, m, s
            if (days > 0) {
                setTimeLeft(`${days} hari ${hours}j ${minutes}m`);
            } else {
                setTimeLeft(`${hours}j ${minutes}m ${seconds}s`);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [expiryDate, s]);

    const getStatusTheme = () => {
        if (s === "paid") return {
            gradient: "from-emerald-600 to-teal-700",
            lightBg: "bg-emerald-50 dark:bg-emerald-500/5",
            icon: <CheckCircle2 className="w-12 h-12 text-white" />,
            label: "Pembayaran Berhasil",
            sublabel: "Transaksi telah diverifikasi sistem",
            accent: "text-emerald-600 dark:text-emerald-400"
        };
        if (s === "pending" || s === "unpaid") return {
            gradient: "from-amber-500 to-orange-600",
            lightBg: "bg-amber-50 dark:bg-amber-500/5",
            icon: <Clock className="w-12 h-12 text-white" />,
            label: "Menunggu Pembayaran",
            sublabel: "Selesaikan sebelum batas waktu",
            accent: "text-orange-600 dark:text-orange-400"
        };
        return {
            gradient: "from-red-500 to-rose-700",
            lightBg: "bg-red-50 dark:bg-red-500/5",
            icon: <XCircle className="w-12 h-12 text-white" />,
            label: "Transaksi Gagal",
            sublabel: "Gagal memproses pembayaran",
            accent: "text-red-600 dark:text-red-400"
        };
    };

    const theme = getStatusTheme();

    const formatPrice = (val: number) => {
        return new Intl.NumberFormat(isUsd ? "en-US" : "id-ID", {
            style: "currency",
            currency: isUsd ? "USD" : "IDR",
            minimumFractionDigits: isUsd ? 2 : 0,
        }).format(val);
    };

    return (
        <div className="w-full mx-auto font-sans">
            <Card className="border-none shadow-2xl dark:shadow-none bg-white dark:bg-slate-950 overflow-hidden p-0">
                <CardContent className="p-0">
                    <div className="flex flex-col lg:flex-row">

                        {/* LEFT SIDE: Status Banner */}
                        <div className={cn(
                            "w-full lg:w-[35%] p-8 lg:p-10 flex flex-col justify-between relative overflow-hidden bg-gradient-to-br",
                            theme.gradient
                        )}>
                            <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse" />

                            <div className="relative z-10">
                                <div className="bg-white/20 w-fit p-4 rounded-2xl backdrop-blur-md mb-6 ring-1 ring-white/30">
                                    {theme.icon}
                                </div>
                                <h2 className="text-2xl lg:text-3xl font-extrabold text-white leading-tight tracking-tight">
                                    {theme.label}
                                </h2>
                                <p className="text-white/80 text-sm mt-2 font-medium">
                                    {theme.sublabel}
                                </p>

                                {(s === "unpaid" || s === "pending") && timeLeft && (
                                    <div className="mt-8 bg-black/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 flex items-center gap-3">
                                        <Timer className="w-4 h-4 text-white" />
                                        <div>
                                            <p className="text-[9px] font-bold text-white/60 uppercase tracking-widest">Sisa Waktu</p>
                                            <p className="text-white font-mono font-bold text-base">{timeLeft}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="relative z-10 mt-12 space-y-5">
                                <div className="pt-5 border-t border-white/20">
                                    <p className="text-[9px] font-bold text-white/50 uppercase tracking-[0.2em] mb-1">Nomor Invoice</p>
                                    <p className="text-white font-mono text-xs tracking-wider opacity-90">{transactionId}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] font-bold text-white/50 uppercase tracking-[0.2em] mb-1">Diterbitkan Pada</p>
                                    <p className="text-white text-xs font-semibold opacity-90">{date}</p>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT SIDE: Breakdown */}
                        <div className="w-full lg:w-[65%] p-8 lg:p-12 bg-white dark:bg-slate-900/40">
                            <div className="mb-12">
                                <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] mb-3">Total Tagihan</p>
                                <h3 className="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tighter">
                                    {formatPrice(total)}
                                </h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
                                {/* Item Group */}
                                {[
                                    { label: "Produk & Layanan", value: packageName, icon: Package, color: "text-blue-500" },
                                    { label: "Opsi Pembayaran", value: method, icon: CreditCard, color: "text-purple-500" },
                                    { label: "Wilayah Operasional", value: isUsd ? "International" : "Indonesia", icon: Globe, color: "text-amber-500" },
                                    // { label: "Keamanan", value: isUsd ? "PCI-DSS" : "ISO 27001", icon: ShieldCheck, color: "text-emerald-500", suffix: true }
                                ].map((item, idx) => (
                                    <div key={idx} className="group">
                                        <div className="flex items-center gap-2 text-slate-400 mb-2 transition-colors group-hover:text-slate-600">
                                            <item.icon className="w-3.5 h-3.5" />
                                            <span className="text-[9px] font-bold uppercase tracking-widest">{item.label}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <p className="text-sm lg:text-base font-bold text-slate-800 dark:text-slate-100 leading-tight">
                                                {item.value}
                                            </p>
                                            {/* {item.suffix && <ChevronRight className="w-3 h-3 text-emerald-500" />} */}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Note Box */}
                            <div className={cn("mt-12 p-5 rounded-2xl flex items-start gap-4 border border-transparent ring-1 ring-inset ring-black/5 dark:ring-white/5", theme.lightBg)}>
                                <AlertCircle className={cn("w-5 h-5 shrink-0 mt-0.5", theme.accent)} />
                                <div className="space-y-1">
                                    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500 opacity-70">Pesan Sistem:</p>
                                    <p className="text-xs font-medium text-slate-600 dark:text-slate-400 leading-relaxed italic">
                                        {s === "paid"
                                            ? "E-Receipt telah diterbitkan. Simpan invoice ini sebagai bukti administrasi sah."
                                            : "Tagihan ini resmi. Pastikan nominal sesuai agar verifikasi berjalan otomatis."}
                                    </p>
                                </div>
                            </div>
                        </div>

                    </div>
                </CardContent>
            </Card>
        </div>
    );
}